import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import { InputGroup } from 'reactstrap';
import { SketchPicker } from 'react-color';
import { FaPaintBrush } from 'react-icons/fa';
import { LabelItem } from '../../../components';
import { InputStyled, InputGroupTextStyled } from '../../../components/Forms/InputStyled';
import useOutsideClick from '../../../common/use-outside-click';
import theme from '../../../assets/css/theme';
import ColorPickerIcon from '../../../assets/img/icons/color-picker-icon.svg?react';

const ColorIndicator = styled.div<{ color: string; $newDesign?: boolean }>`
  background: ${(props) => props.color || 'url(/assets/images/transparent-bcg.png)'};
  background-size: contain;
  border-radius: 50%;
  border: 1px solid ${(props) => (props.$newDesign ? '#e5e5e5' : 'black')};
  flex-grow: 0;
  flex-shrink: 0;
  flex-basis: ${(props) => (props.$newDesign ? '36px' : '33px')};
  height: ${(props) => (props.$newDesign ? '36px' : '33px')};
  cursor: pointer;
  // Inset the fill so the 1px ring reads as a separate outline around the swatch.
  ${(props) =>
    props.$newDesign &&
    `padding: 3px;
    background-clip: content-box;`}
`;

const Popover = styled.div`
  position: absolute;
  z-index: 99999;
`;

const NewDesignWrapper = styled.div`
  // Fills its grid cell and pins label + input to the bottom, so a label that wraps to
  // two lines does not push its input below the one next to it, and a one-line label
  // sits on the wrapped label's last line.
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  height: 100%;

  .input-item-group input {
    height: 44px;
    // Rounded on the left only; the brush addon closes the pill on the right.
    border-radius: 22px 0 0 22px;
    font-size: ${theme.fontSizes.body};
  }

  .input-item-group .input-group-text {
    height: 44px;
  }

  label {
    font-size: ${theme.fontSizes.body};
    font-weight: 400;
  }
`;

const PickerIcon = styled(ColorPickerIcon)`
  width: 15px;
  height: 15px;
`;

type Props = {
  color: string;
  id: string;
  label?: string;
  labelActions?: any;
  placeholder?: string;
  onColorChange: (...args: any[]) => void;
  onColorChangeComplete?: (...args: any[]) => void;
  style?: { [key: string]: string };
  newDesign?: boolean;
  disabled?: boolean;
};

const ColorPickerField = (props: Props) => {
  const wrapperRef = useRef(null);
  const [showPicker, setShowPicker] = useState(false);
  useOutsideClick(wrapperRef, () => setShowPicker(false));

  const Wrapper = props.newDesign ? NewDesignWrapper : 'div';

  const togglePicker = () => {
    if (!props.disabled) {
      setShowPicker((current) => !current);
    }
  };

  return (
    <Wrapper ref={wrapperRef}>
      <div
        className="d-flex justify-content-between align-items-center"
        style={{
          paddingRight: '15px',
          marginBottom: props.newDesign ? '15px' : undefined
        }}
      >
        <LabelItem
          htmlFor={props.id}
          isSmall
          isBold={!props.newDesign}
          margin={props.newDesign ? '0' : undefined}
        >
          {props.label}
        </LabelItem>
        {props.labelActions}
      </div>
      <div id={props.id} className="d-flex align-items-center">
        <ColorIndicator color={props.color} $newDesign={props.newDesign} onClick={togglePicker} />
        <div className="input-item-group ml-2" style={{ width: '100%', zIndex: 1 }}>
          <InputGroup>
            <InputStyled
              disabled={props.disabled}
              onChange={(e: any) => {
                if (!e.target.value.startsWith('#')) return;
                props.onColorChange({ hex: e.target.value });
                if (props.onColorChangeComplete) {
                  props.onColorChangeComplete({ hex: e.target.value });
                }
              }}
              value={props.color}
              placeholder={props.placeholder}
            />
            <InputGroupTextStyled
              role="button"
              aria-label={props.label}
              aria-expanded={showPicker}
              tabIndex={props.disabled ? -1 : 0}
              style={{ cursor: props.disabled ? undefined : 'pointer' }}
              onClick={togglePicker}
              onKeyDown={(e: React.KeyboardEvent) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  togglePicker();
                }
              }}
            >
              {props.newDesign ? (
                <PickerIcon aria-hidden />
              ) : (
                <FaPaintBrush color={theme.commonColors.second} size={15} />
              )}
            </InputGroupTextStyled>
          </InputGroup>
        </div>
      </div>
      {showPicker && !props.disabled && (
        <Popover style={props.style}>
          <SketchPicker
            color={props.color}
            onChange={props.onColorChange}
            onChangeComplete={props.onColorChangeComplete}
            disableAlpha
          />
        </Popover>
      )}
    </Wrapper>
  );
};

export default ColorPickerField;
